import type {
  CardInstance,
  Effect,
  GameState,
  InstanceId,
  JsonValue,
  PlayerId,
} from '@cards/engine';
import { HIDDEN_CARD_DEF_ID, parseCellId } from '@cards/engine';
import {
  getCardUi,
  type GameCardUi,
  type GameCardUiContext,
  type PublicCardFact,
  type PublicCardRelation,
  type PublicVisibleEffectView,
} from '@cards/cards';

function relation(playerId: PlayerId, viewerId: PlayerId): PublicCardRelation {
  return playerId === viewerId ? 'viewer' : 'opponent';
}

export function projectPublicFacts(
  vars: Readonly<Record<string, JsonValue>>,
  ui: GameCardUi | undefined,
): Readonly<Record<string, PublicCardFact>> {
  const facts: Record<string, PublicCardFact> = {};
  for (const [alias, projection] of Object.entries(ui?.publicVars ?? {})) {
    const value = vars[projection.key];
    if (projection.kind === 'presence') {
      facts[alias] = value !== undefined && value !== null;
    } else if (projection.kind === 'boolean' && typeof value === 'boolean') {
      facts[alias] = value;
    } else if (projection.kind === 'number' && typeof value === 'number' && Number.isFinite(value)) {
      facts[alias] = value;
    }
  }
  return Object.freeze(facts);
}

function effectCoversInstance(effect: Effect, instance: CardInstance): boolean {
  switch (effect.scope.kind) {
    case 'unit':
      return effect.scope.instanceId === instance.id;
    case 'player':
      return effect.scope.playerId === instance.controllerId;
    case 'cell':
      return instance.cellId === effect.scope.cellId;
    case 'lane': {
      if (instance.cellId === undefined) return false;
      const cell = parseCellId(instance.cellId);
      return cell.tableId === effect.scope.tableId && cell.laneIndex === effect.scope.laneIndex;
    }
    case 'table':
      return instance.cellId !== undefined && parseCellId(instance.cellId).tableId === effect.scope.tableId;
  }
}

function visibleSourceDefinition(state: GameState, effect: Effect): string | undefined {
  if (effect.sourceId === undefined) return undefined;
  const definitionId = state.instances[effect.sourceId]?.defId;
  return definitionId === undefined || definitionId === HIDDEN_CARD_DEF_ID ? undefined : definitionId;
}

function visibleEffects(state: GameState, instance: CardInstance): readonly PublicVisibleEffectView[] {
  return Object.freeze(state.effects
    .filter((effect) => effectCoversInstance(effect, instance))
    .map((effect, index) => {
      const sourceDefinitionId = visibleSourceDefinition(state, effect);
      return Object.freeze({
        key: `effect-${index}`,
        scopeKind: effect.scope.kind,
        definitionId: effect.defId,
        ...(sourceDefinitionId === undefined ? {} : { sourceDefinitionId }),
        ...(effect.duration === undefined ? {} : { duration: effect.duration }),
        persistent: effect.persistent === true,
        ...(effect.shield === undefined ? {} : {
          shield: Object.freeze({
            remaining: effect.shield.remaining,
            ...(effect.shield.onDepletedCool === undefined
              ? {} : { onDepletedCool: effect.shield.onDepletedCool }),
          }),
        }),
        statMods: Object.freeze((effect.statMods ?? []).map((modifier) => Object.freeze({ ...modifier }))),
        facts: projectPublicFacts(effect.vars, getCardUi(effect.defId)),
      });
    }));
}

/**
 * The sole cards/web boundary for status providers. Production callers pass
 * the already-redacted client snapshot; the returned DTO additionally strips
 * every player/instance/effect/source id and all undeclared var values.
 */
export function gameCardUiContextFromRedactedSnapshot(
  snapshot: GameState,
  instanceId: InstanceId,
  viewerId: PlayerId,
): GameCardUiContext | undefined {
  const instance = snapshot.instances[instanceId];
  const viewer = snapshot.players[viewerId];
  if (instance === undefined || viewer === undefined || instance.defId === HIDDEN_CARD_DEF_ID) return undefined;
  const controllerId = instance.controllerId ?? instance.ownerId;
  const cell = instance.cellId === undefined ? undefined : parseCellId(instance.cellId);
  const controllerSide = snapshot.players[controllerId]?.side;
  const fullyFurnished = cell === undefined || controllerSide === undefined
    ? false
    : Object.values(snapshot.board.cells).filter((candidate) =>
      candidate.tableId === cell.tableId && candidate.side === controllerSide
      && candidate.occupantId !== null
      && snapshot.instances[candidate.occupantId]?.controllerId === controllerId).length === 3;
  const timedDestroy = snapshot.timedDestroys.find((marker) => marker.instanceIds.includes(instanceId));
  const capture = snapshot.captureReservations.find((reservation) => reservation.targetInstanceId === instanceId);
  const damagedPlayerIds = instance.vars.damagedPlayerIds;
  return Object.freeze({
    round: snapshot.round,
    phase: snapshot.phase,
    card: Object.freeze({
      definitionId: instance.defId,
      zone: instance.zone,
      owner: relation(instance.ownerId, viewerId),
      controller: relation(controllerId, viewerId),
      stats: Object.freeze({ ...instance.stats }),
      heat: instance.heat ?? 0,
      freeze: instance.freeze ?? 0,
      isToken: instance.isToken === true,
      hacked: instance.hack !== undefined,
      ...(cell === undefined ? {} : {
        cell: Object.freeze({
          table: cell.tableId,
          lane: cell.laneIndex,
          side: cell.side === viewer.side ? 'viewer' as const : 'opponent' as const,
          fullyFurnished,
        }),
      }),
      facts: projectPublicFacts(instance.vars, getCardUi(instance.defId)),
    }),
    visibleEffects: visibleEffects(snapshot, instance),
    viewer: Object.freeze({
      ownsCard: instance.ownerId === viewerId,
      controlsCard: controllerId === viewerId,
      wasDamagedByCard: Array.isArray(damagedPlayerIds) && damagedPlayerIds.includes(viewerId),
    }),
    ...(timedDestroy === undefined ? {} : {
      timedDestroy: Object.freeze({
        key: 'timed-destroy',
        deadlineRound: timedDestroy.deadlineRound,
        remainingRounds: Math.max(0, timedDestroy.deadlineRound - snapshot.round),
      }),
    }),
    ...(capture === undefined ? {} : {
      capture: Object.freeze({
        expiresRound: capture.expiresRound,
        returnFreeze: capture.returnFreeze,
      }),
    }),
  });
}
